import type { ChemIsomerV1Document } from '../domain/isomerDocument.ts';
import type { ChemMoleculeV1Document } from '../domain/moleculeProjection.ts';
import { copyFragment, pasteFragment, type ClipboardFragment } from '../domain/clipboard.ts';
import { createEmptyDrawingDocument } from '../domain/drawingDocumentFactory.ts';
import type { PaperSize } from '../domain/geometry.ts';
import { reconstructDrawingDocument } from '../domain/moleculeDrawingProjection.ts';
import { regionalDefaultPage } from '../domain/page.ts';
import type { Annotation, DrawingDocument, ReactionArrowAnnotation } from '../model';
import { placedOnPage } from '../rendering/contentExtent.ts';
import { generateIsomerDepiction } from './isomerDepiction.ts';
import type { ParticipantRole, ReactionDrawingDocument } from './reactionParticipant.ts';

/**
 * ADR 0025 (#1140-#1142) -- a saved reaction's paper, rebuilt: the one place it is done, for the
 * workspace (Open, Add to paper), the cards and /r/ page, and the render service's pictures.
 */

/** What drawing a reaction takes: its document and each participant's molecule and isomer. */
export interface ReactionToDraw {
  readonly id: string;
  readonly document: ReactionDrawingDocument;
  readonly participants: readonly {
    readonly role: ParticipantRole;
    readonly molecule: { readonly document: ChemMoleculeV1Document };
    readonly isomer: { readonly document: ChemIsomerV1Document };
  }[];
}

export interface ReactionPaper {
  readonly document: DrawingDocument;
  /** The atoms of every catalyst participant (for the reagent / catalyst marks). */
  readonly catalystAtomIds: readonly string[];
}

/** Paper ids to the ids the same atoms / bonds have elsewhere (a pasted copy, a saved molecule). */
export interface IdMap {
  readonly atoms: ReadonlyMap<string, string>;
  readonly bonds: ReadonlyMap<string, string>;
}

/** Pastes `fragment` into `target` with its centre where `points` (its atoms) are centred. */
export function pasteAtItsPlace(
  target: DrawingDocument,
  fragment: ClipboardFragment,
  points: readonly { readonly x: number; readonly y: number }[],
  size: { readonly width: number; readonly height: number }
): { document: DrawingDocument } & IdMap {
  const xs = points.map((point) => point.x);
  const ys = points.map((point) => point.y);
  const centre = {
    x: (Math.min(...xs) + Math.max(...xs)) / 2 / size.width,
    y: (Math.min(...ys) + Math.max(...ys)) / 2 / size.height
  };
  const pasted = pasteFragment(target, fragment, centre);
  return {
    document: pasted.document,
    atoms: new Map(Object.keys(fragment.atoms).map((id, index) => [id, pasted.pastedAtomIds[index]!])),
    bonds: new Map(Object.keys(fragment.bonds).map((id, index) => [id, pasted.pastedBondIds[index]!]))
  };
}

/** `value` with its atom and bond references renamed through `ids` (unknown ones kept). */
export function renamed<T>(value: T, ids: IdMap): T {
  const atom = (id: string) => ids.atoms.get(id) ?? id;
  const bond = (id: string) => ids.bonds.get(id) ?? id;
  const walk = (item: unknown): unknown => {
    if (Array.isArray(item)) return item.map(walk);
    if (!item || typeof item !== 'object') return item;
    return Object.fromEntries(
      Object.entries(item).map(([key, inner]) => [
        key,
        key === 'atomId' && typeof inner === 'string'
          ? atom(inner)
          : key === 'bondId' && typeof inner === 'string'
            ? bond(inner)
            : key === 'atomIds' && Array.isArray(inner)
              ? (inner as string[]).map(atom)
              : key === 'bondIds' && Array.isArray(inner)
                ? (inner as string[]).map(bond)
                : walk(inner)
      ])
    );
  };
  return walk(value) as T;
}

/** Room (drawing units) between laid-out molecules, the arrow and the edges. */
const GAP = 60;
const MARGIN = 80;
const MIN_ARROW = 160;

interface Piece {
  readonly role: ParticipantRole;
  readonly fragment: ClipboardFragment;
  readonly width: number;
  readonly height: number;
}

function extentOf(fragment: ClipboardFragment, size: { readonly width: number; readonly height: number }) {
  const points = Object.values(fragment.positions);
  const span = (values: readonly number[]) => (values.length ? Math.max(...values) - Math.min(...values) : 0);
  return {
    width: span(points.map((point) => point.x)) * (fragment.size?.width ?? size.width),
    height: span(points.map((point) => point.y)) * (fragment.size?.height ?? size.height)
  };
}

const rowWidth = (pieces: readonly Piece[]) =>
  pieces.reduce((sum, piece) => sum + piece.width, 0) + GAP * Math.max(0, pieces.length - 1);
const rowHeight = (pieces: readonly Piece[]) => Math.max(0, ...pieces.map((piece) => piece.height));

/**
 * A saved reaction laid out on a fresh paper: reactants in a row, the arrow, products in a row;
 * agents (reagents, catalysts) above the arrow, solvents below it. The boxes then form around the
 * arrow, each molecule in the box its role belongs to.
 */
export async function reactionPaper(reaction: ReactionToDraw, paperSize: PaperSize): Promise<ReactionPaper> {
  if (reaction.document.canvas) return storedPaper(reaction, reaction.document.canvas);
  const pieces: Piece[] = [];
  for (const participant of reaction.participants) {
    const drawn = await generateIsomerDepiction(participant.molecule.document, participant.isomer.document, paperSize);
    const fragment = copyFragment(
      drawn,
      drawn.structure.graph.nodes.map((node) => node.id)
    );
    if (!fragment) continue;
    pieces.push({ role: participant.role, fragment, ...extentOf(fragment, drawn.depiction.coordinateSet) });
  }
  const of = (...roles: ParticipantRole[]) => pieces.filter((piece) => roles.includes(piece.role));
  const reactants = of('reactant');
  const products = of('product');
  const agents = of('reagent', 'catalyst');
  const solvents = of('solvent');

  const arrowLength = Math.max(MIN_ARROW, rowWidth(agents), rowWidth(solvents));
  const middleHeight = Math.max(rowHeight(reactants), rowHeight(products), GAP);
  const top = agents.length ? rowHeight(agents) + GAP : 0;
  const bottom = solvents.length ? rowHeight(solvents) + GAP : 0;
  const midY = MARGIN + Math.max(top, middleHeight / 2);
  const arrowFrom = { x: MARGIN + rowWidth(reactants) + (reactants.length ? GAP : 0), y: midY };
  const arrowTo = { x: arrowFrom.x + arrowLength, y: midY };
  const width = arrowTo.x + (products.length ? GAP + rowWidth(products) : 0) + MARGIN;
  const height = midY + Math.max(bottom, middleHeight / 2) + MARGIN;

  // Each piece's centre, row by row.
  const centres = new Map<Piece, { x: number; y: number }>();
  const placeRow = (row: readonly Piece[], startX: number, y: number) => {
    let x = startX;
    for (const piece of row) {
      centres.set(piece, { x: x + piece.width / 2, y });
      x += piece.width + GAP;
    }
  };
  placeRow(reactants, MARGIN, midY);
  placeRow(products, arrowTo.x + GAP, midY);
  const arrowMidX = (arrowFrom.x + arrowTo.x) / 2;
  placeRow(agents, arrowMidX - rowWidth(agents) / 2, midY - GAP - rowHeight(agents) / 2);
  placeRow(solvents, arrowMidX - rowWidth(solvents) / 2, midY + GAP + rowHeight(solvents) / 2);

  let document = createEmptyDrawingDocument({ width, height });
  const catalystAtomIds: string[] = [];
  for (const piece of pieces) {
    const centre = centres.get(piece)!;
    const pasted = pasteFragment(document, piece.fragment, { x: centre.x / width, y: centre.y / height });
    document = pasted.document;
    if (piece.role === 'catalyst') catalystAtomIds.push(...pasted.pastedAtomIds);
  }
  const arrow = reaction.document.arrow ?? { kind: 'forward' as const };
  const annotation: ReactionArrowAnnotation = {
    id: `reaction-arrow-${reaction.id}`,
    kind: 'reaction-arrow',
    arrowKind: arrow.kind,
    ...(arrow.overlay ? { overlay: arrow.overlay } : {}),
    from: { kind: 'point', at: arrowFrom },
    to: { kind: 'point', at: arrowTo }
  };
  document = { ...document, annotations: [...document.annotations, annotation] };
  // #772 -- the reaction's page comes back with it.
  const page = reaction.document.page;
  return { document: page ? { ...document, page } : document, catalystAtomIds };
}

/**
 * #1140 (ADR 0025 D3): a reaction saved with its paper, back as it was drawn -- each participant
 * where its depiction says, with its annotations, and the canvas's arrow, text and shapes.
 */
function storedPaper(reaction: ReactionToDraw, canvas: NonNullable<ReactionDrawingDocument['canvas']>): ReactionPaper {
  let document = createEmptyDrawingDocument({ width: canvas.width, height: canvas.height });
  const annotations: Annotation[] = [];
  const catalystAtomIds: string[] = [];
  reaction.participants.forEach((expanded, index) => {
    const saved = reaction.document.participants[index]!;
    // #1137: shown as the text typed by the arrow (that text is on the canvas), not drawn.
    if (saved.shownAs) return;
    const own = reconstructDrawingDocument(
      {
        schemaVersion: 'chem-drawing.v11',
        kind: 'molecule',
        isomerRefs: [
          {
            isomerId: saved.isomerId,
            isomerRevision: saved.isomerRevision,
            depiction: saved.depiction,
            annotations: []
          }
        ]
      },
      expanded.molecule.document,
      expanded.isomer.document
    );
    const atomIds = own.structure.graph.nodes.map((node) => node.id);
    const fragment = copyFragment(own, atomIds);
    if (!fragment) return;
    const { coordinates, width, height } = own.depiction.coordinateSet;
    const pasted = pasteAtItsPlace(
      document,
      fragment,
      atomIds.map((id) => coordinates[id]!),
      { width, height }
    );
    document = pasted.document;
    annotations.push(...saved.annotations.map((annotation) => renamed(annotation, pasted)));
    if (saved.role === 'catalyst') catalystAtomIds.push(...pasted.atoms.values());
  });
  document = { ...document, annotations: [...document.annotations, ...annotations, ...canvas.annotations] };
  // #1143 (ADR 0025 D7): saved as its drawing's area, it's set in from the page's corner, on the
  // reader's own page (a reaction keeps no page since #1141; an older one keeps the one it has).
  return {
    document: { ...placedOnPage(document), page: reaction.document.page ?? regionalDefaultPage() },
    catalystAtomIds
  };
}
